import { useEffect, useMemo, useRef, useState } from 'react'
import { Trash2 } from 'lucide-react'
import { toast } from 'sonner'
import {
  EditableGrid,
  cellKey,
  type EditableColumnDef,
} from '@/components/ui/EditableGrid/EditableGrid'
import type { PickerValue } from '@/components/ui/ProductPicker/ProductPicker'
import { PricelistAwareProductPicker } from './PricelistAwareProductPicker'
import { ApiError } from '@/lib/api/jsonFetch'
import {
  useCreateForecastLine,
  useDeleteForecastLine,
  usePricelistProducts,
  useUpdateForecastLine,
} from '../api/queries'
import type { ForecastLine } from '../api/types'

const MODULE_ID = 'demand-forecast'

/**
 * Immediate-commit entry grid — mirrors customer-pricelists'
 * PricelistEditor.tsx pattern (see that file for the fuller precedent):
 * draft rows POST once the minimum required fields are set, existing-row
 * cells PATCH individually on commit, 400s route to `cellErrors` on the
 * specific cell (the server names the conflicting line for an overlap
 * rejection), other errors surface as a toast.
 *
 * Unlike Pricelists, `product_id` stays editable on existing rows too —
 * the backend's `_LINE_EDITABLE_FIELDS` allows it (no Odoo-side identity
 * concern the way a pricelist item's product linkage has).
 */

interface DraftRow {
  id: number // synthetic negative sentinel — never hits the API
  _draft: true
  product_id: PickerValue
  /** Display-only cache, set when auto-seeded from the pricelist; blank
   *  for a manually-added draft row until the picker resolves one. */
  product_default_code: string | null
  period_start: string | null
  period_end: string | null
  qty: number | null
  notes: string | null
  /** Auto-seeded from the pricelist (not added by the user). An untouched
   *  seeded row is hidden once its product gets a committed line — e.g.
   *  after a spreadsheet import. */
  _seeded?: true
}

type GridRow = (ForecastLine & { _draft?: false }) | DraftRow

function isDraft(row: GridRow): row is DraftRow {
  return (row as DraftRow)._draft === true
}

let draftCounter = -1
function makeDraftRow(): DraftRow {
  return {
    id: draftCounter--,
    _draft: true,
    product_id: null,
    product_default_code: null,
    period_start: null,
    period_end: null,
    qty: null,
    notes: null,
  }
}

export interface ForecastLinesGridProps {
  customerId: number
  lines: ForecastLine[]
  /** True while the customer's committed lines are still loading. Gates
   *  the pricelist auto-populate seed below — seeding against `lines=[]`
   *  before it's genuinely loaded would duplicate rows for products that
   *  already have a committed line once the real data arrives. */
  linesLoading?: boolean
}

export function ForecastLinesGrid({ customerId, lines, linesLoading = false }: ForecastLinesGridProps) {
  const createLine = useCreateForecastLine()
  const updateLine = useUpdateForecastLine()
  const deleteLine = useDeleteForecastLine()
  const { data: pricelistProducts } = usePricelistProducts(customerId)

  const [draftRows, setDraftRows] = useState<DraftRow[]>([])
  const [pendingEdits, setPendingEdits] = useState<Map<number, Partial<ForecastLine>>>(new Map())
  const [cellErrors, setCellErrors] = useState<Map<string, string>>(new Map())

  // Auto-populate one draft row per pricelist product on first load for
  // this customer, per stakeholder feedback — skips products that
  // already have a committed line so the rep doesn't see confusing
  // duplicates. Runs once per customer (this component remounts on
  // customer change via ForecastEntryPage's `key={effectiveCustomerId}`),
  // gated on both the pricelist fetch AND the real committed lines
  // having loaded (not just the parent's placeholder `[]`).
  const seededRef = useRef(false)
  useEffect(() => {
    if (seededRef.current || linesLoading || !pricelistProducts) return
    seededRef.current = true
    const existingProductIds = new Set(lines.map((l) => l.product_id))
    const seeded: DraftRow[] = pricelistProducts
      .filter((p) => !existingProductIds.has(p.id))
      .map((p) => ({
        id: draftCounter--,
        _draft: true,
        _seeded: true,
        product_id: [p.id, p.name] as PickerValue,
        product_default_code: p.default_code || null,
        period_start: null,
        period_end: null,
        qty: null,
        notes: null,
      }))
    if (seeded.length) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- one-time seed from the pricelist fetch + committed lines, both external data; gated by seededRef so it can never loop.
      setDraftRows((prev) => [...seeded, ...prev])
    }
  }, [pricelistProducts, linesLoading, lines])

  const rows: GridRow[] = useMemo(() => {
    const merged: GridRow[] = lines.map((line) => ({
      ...line,
      ...(pendingEdits.get(line.id) ?? {}),
    }))
    const committed = new Set(lines.map((l) => l.product_id))
    const visibleDrafts = draftRows.filter(
      (d) =>
        !(
          d._seeded &&
          d.product_id &&
          committed.has(d.product_id[0]) &&
          d.period_start === null &&
          d.period_end === null &&
          d.qty === null &&
          !d.notes
        ),
    )
    return [...merged, ...visibleDrafts]
  }, [lines, pendingEdits, draftRows])

  const columns: EditableColumnDef<GridRow>[] = useMemo(
    () => [
      {
        key: 'product_id',
        header: 'Product',
        width: '28%',
        render: (_value, row) => {
          const label = isDraft(row) ? row.product_id?.[1] : row.product_name
          if (!label) return <span className="text-tertiary">Pick product…</span>
          const code = row.product_default_code
          return (
            <span className="flex flex-col truncate">
              <span className="truncate">{label}</span>
              {code ? <span className="text-role-metadata text-tertiary">{code}</span> : null}
            </span>
          )
        },
        renderEditor: ({ value: _value, onCommit, onCancel }) => (
          <PricelistAwareProductPicker
            customerId={customerId}
            value={null}
            onChange={(next) => onCommit(next)}
            editorMode
            onCancel={onCancel}
          />
        ),
      },
      {
        key: 'period_start',
        header: 'Period start',
        width: '14%',
        render: (v) => (v as string | null) || <span className="text-tertiary">—</span>,
        renderEditor: ({ value, onCommit, onCancel }) => (
          <DateEditor value={value as string | null} onCommit={onCommit} onCancel={onCancel} />
        ),
      },
      {
        key: 'period_end',
        header: 'Period end',
        width: '14%',
        render: (v) => (v as string | null) || <span className="text-tertiary">—</span>,
        renderEditor: ({ value, onCommit, onCancel }) => (
          <DateEditor value={value as string | null} onCommit={onCommit} onCancel={onCancel} />
        ),
      },
      {
        key: 'qty',
        header: 'Qty',
        numeric: true,
        width: '12%',
        render: (v, row) => {
          const raw = isDraft(row) ? row.qty : v
          return raw == null || raw === '' ? <span className="text-tertiary">—</span> : String(raw)
        },
      },
      {
        key: 'notes',
        header: 'Notes',
        width: '32%',
        render: (v) => (v as string | null) || <span className="text-tertiary">—</span>,
      },
    ],
    [customerId],
  )

  const colIdxFor = (key: string | number | symbol): number =>
    columns.findIndex((c) => c.key === key)

  function extractErrorMessage(err: unknown): string {
    if (err instanceof ApiError) {
      // The overlap-rejection detail is a plain string naming the
      // conflicting line — surface it as-is rather than stripping a
      // prefix that isn't present on this endpoint's error shape.
      return err.message
    }
    return String(err)
  }

  function routeMutationError(rowIdx: number, key: string | number | symbol, err: unknown): void {
    const colIdx = colIdxFor(key)
    if (err instanceof ApiError && (err.status === 400 || err.status === 403)) {
      setCellErrors((prev) => {
        const next = new Map(prev)
        next.set(cellKey(rowIdx, colIdx), extractErrorMessage(err))
        return next
      })
    } else {
      toast.error(`Save failed: ${extractErrorMessage(err)}`)
    }
  }

  function clearCellError(rowIdx: number, key: string | number | symbol): void {
    const k = cellKey(rowIdx, colIdxFor(key))
    setCellErrors((prev) => {
      if (!prev.has(k)) return prev
      const next = new Map(prev)
      next.delete(k)
      return next
    })
  }

  function applyPendingEdit(lineId: number, patch: Partial<ForecastLine>) {
    setPendingEdits((prev) => {
      const next = new Map(prev)
      next.set(lineId, { ...(next.get(lineId) ?? {}), ...patch })
      return next
    })
  }

  function clearPendingEdit(lineId: number, key: keyof ForecastLine) {
    setPendingEdits((prev) => {
      const existing = prev.get(lineId)
      if (!existing || !(key in existing)) return prev
      const next = new Map(prev)
      const updated = { ...existing }
      delete updated[key]
      if (Object.keys(updated).length === 0) next.delete(lineId)
      else next.set(lineId, updated)
      return next
    })
  }

  function commitDraftCell(row: DraftRow, key: keyof DraftRow, value: unknown, rowIdx: number) {
    const next: DraftRow = { ...row, [key]: value } as DraftRow
    if (key === 'product_id') {
      // The picker only returns [id, name] — clear the (now possibly
      // stale, e.g. from an auto-seeded row whose product was changed)
      // cached code rather than keep showing the OLD product's code.
      next.product_default_code = null
      delete next._seeded
    }
    setDraftRows((prev) => prev.map((r) => (r.id === row.id ? next : r)))

    const productId = next.product_id ? next.product_id[0] : null
    const hasQty = typeof next.qty === 'number' && !Number.isNaN(next.qty)
    if (productId !== null && next.period_start && next.period_end && hasQty) {
      createLine.mutate(
        {
          customer_id: customerId,
          product_id: productId,
          period_start: next.period_start,
          period_end: next.period_end,
          qty: next.qty as number,
          notes: next.notes,
        },
        {
          onSuccess: () => {
            setDraftRows((prev) => prev.filter((r) => r.id !== row.id))
          },
          onError: (err) => routeMutationError(rowIdx, key, err),
        },
      )
    }
  }

  const handleCellChange = (rowIdx: number, key: keyof GridRow, value: unknown): void => {
    const row = rows[rowIdx]
    if (!row) return
    clearCellError(rowIdx, key)

    if (isDraft(row)) {
      const wireValue = key === 'product_id' ? (value as PickerValue) : value
      commitDraftCell(row, key as keyof DraftRow, wireValue, rowIdx)
      return
    }

    const lineKey = key as keyof ForecastLine
    const isProductEdit = lineKey === 'product_id'
    const picked = isProductEdit ? (value as PickerValue) : null
    const wireValue = isProductEdit ? (picked ? picked[0] : null) : value

    applyPendingEdit(row.id, {
      [lineKey]: wireValue,
      ...(isProductEdit ? { product_name: picked ? picked[1] : null } : {}),
    } as Partial<ForecastLine>)

    updateLine.mutate(
      { lineId: row.id, customerId, edits: { [lineKey]: wireValue } },
      {
        onSuccess: () => clearPendingEdit(row.id, lineKey),
        onError: (err) => {
          clearPendingEdit(row.id, lineKey)
          routeMutationError(rowIdx, key, err)
        },
      },
    )
  }

  const handleAddRow = () => {
    setDraftRows((prev) => [...prev, makeDraftRow()])
  }

  const handleDeleteRow = (row: GridRow) => {
    if (isDraft(row)) {
      setDraftRows((prev) => prev.filter((r) => r.id !== row.id))
      return
    }
    if (!window.confirm(`Delete this forecast line (${row.product_name ?? 'product'})?`)) return
    deleteLine.mutate(
      { lineId: row.id, customerId },
      { onError: (err) => toast.error(`Delete failed: ${extractErrorMessage(err)}`) },
    )
  }

  const dirtyCells = useMemo(() => {
    const set = new Set<string>()
    for (const [id, patch] of pendingEdits) {
      const rowIdx = rows.findIndex((r) => !isDraft(r) && r.id === id)
      if (rowIdx === -1) continue
      for (const k of Object.keys(patch)) {
        set.add(cellKey(rowIdx, colIdxFor(k)))
      }
    }
    return set
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingEdits, rows])

  const validators = useMemo(
    () => ({
      period_end: (value: unknown, row: GridRow) => {
        if (!value || !row.period_start) return null
        return (value as string) < row.period_start
          ? 'Period end must be on or after period start'
          : null
      },
      qty: (value: unknown) => {
        if (value === null || value === '' || value === undefined) return null
        const n = Number(value)
        if (Number.isNaN(n)) return 'Must be a number'
        return n >= 0 ? null : 'Quantity cannot be negative'
      },
    }),
    [],
  )

  return (
    <EditableGrid<GridRow>
      rows={rows}
      columns={columns}
      onCellChange={handleCellChange}
      onAddRow={handleAddRow}
      addRowLabel="Add forecast line"
      dirtyCells={dirtyCells}
      cellErrors={cellErrors}
      validators={validators}
      moduleId={MODULE_ID}
      minTableWidth="900px"
      rowActions={(row) => (
        <button
          type="button"
          aria-label={isDraft(row) ? 'Discard draft row' : 'Delete forecast line'}
          onClick={() => handleDeleteRow(row)}
          className="inline-flex h-6 w-6 cursor-pointer items-center justify-center rounded-xs text-tertiary hover:bg-danger-bg hover:text-danger-text focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-danger"
        >
          <Trash2 size={14} />
        </button>
      )}
      title={
        <span>
          Forecast lines{' '}
          <span className="text-role-metadata">
            · {rows.length} line{rows.length === 1 ? '' : 's'}
            {dirtyCells.size > 0 ? ` · ${dirtyCells.size} saving` : ''}
          </span>
        </span>
      }
    />
  )
}

function DateEditor({
  value,
  onCommit,
  onCancel,
}: {
  value: string | null
  onCommit: (next: unknown) => void
  onCancel: () => void
}) {
  const [draft, setDraft] = useState<string>(value ?? '')
  return (
    <input
      type="date"
      autoFocus
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => onCommit(draft === '' ? null : draft)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault()
          onCommit(draft === '' ? null : draft)
        } else if (e.key === 'Escape') {
          e.preventDefault()
          onCancel()
        }
      }}
      className="h-full w-full border-none bg-transparent px-[var(--cell-padding-x)] text-sm text-primary outline-none"
    />
  )
}
